import {
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode
} from 'react';

import { type OAuth2Tokens, type RestApiClient } from '@docyrus/api-client';

import {
  type AuthStatus,
  type DocyrusAuthContextValue,
  type DocyrusAuthConfig,
  type DocyrusCalendarEventPayload,
  type DocyrusEmailPayload,
  type DocyrusSharePayload,
  type DocyrusUser,
  type GuidyRoute,
  type HostNavigationHandler,
  type HostNavigationRequestOptions,
  type HostNotificationHandler,
  type PermissionScope,
  type RouteChangePayload
} from '../types';

import { AuthManager } from '../core/auth-manager';
import { hasRole as coreHasRole, hasPermission as coreHasPermission } from '../core/permissions';

import { DocyrusAuthContext } from './auth-context';

export interface DocyrusAuthProviderProps extends DocyrusAuthConfig {
  children: ReactNode;
}

export function DocyrusAuthProvider({
  children,
  ...config
}: DocyrusAuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [tokens, setTokens] = useState<OAuth2Tokens | null>(null);
  const [client, setClient] = useState<RestApiClient | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [user, setUser] = useState<DocyrusUser | null>(null);
  const configRef = useRef(config);
  const managerRef = useRef<AuthManager | null>(null);

  if (!managerRef.current) {
    managerRef.current = new AuthManager(configRef.current);
  }

  const mode = managerRef.current.getMode();

  useEffect(() => {
    const manager = managerRef.current;

    if (!manager) return;

    const unsubscribe = manager.subscribe((state) => {
      setStatus(state.status);
      setTokens(state.tokens);
      setError(state.error);
      setClient(manager.getClient());
      setUser(state.user);
    });

    manager.initialize();

    return () => {
      unsubscribe();
      manager.destroy();
    };
  }, []);

  const signIn = useMemo(() => {
    return () => {
      managerRef.current?.signIn();
    };
  }, []);

  const signInWithTokens = useMemo(() => {
    return async (tokens: OAuth2Tokens) => {
      await managerRef.current?.signInWithTokens(tokens);
    };
  }, []);

  const signOut = useMemo(() => {
    return async () => {
      await managerRef.current?.signOut();
    };
  }, []);

  const getAuthorizationUrl = useMemo(() => {
    return async () => {
      return managerRef.current?.getAuthorizationUrl() ?? null;
    };
  }, []);

  const hasRole = useMemo(() => {
    return (role: string | string[] | null | undefined) => coreHasRole(user, role);
  }, [user]);

  const hasPermission = useMemo(() => {
    return (operation: string, scope?: string | null | PermissionScope) => coreHasPermission(user, operation, scope);
  }, [user]);

  const refreshUser = useMemo(() => {
    return async () => {
      await managerRef.current?.refreshUser();
    };
  }, []);

  const onHostNavigation = useMemo(() => {
    return (handler: HostNavigationHandler) => {
      return managerRef.current?.onHostNavigation(handler) ?? (() => {});
    };
  }, []);

  const onHostNotification = useMemo(() => {
    return (handler: HostNotificationHandler) => {
      return managerRef.current?.onHostNotification(handler) ?? (() => {});
    };
  }, []);

  const enableHostRouteSync = useMemo(() => {
    return () => {
      managerRef.current?.enableHostRouteSync();
    };
  }, []);

  const notifyRouteChange = useMemo(() => {
    return (payload?: RouteChangePayload) => {
      managerRef.current?.notifyHostRouteChange(payload);
    };
  }, []);

  const requestHostNavigation = useMemo(() => {
    return (url: string, options?: HostNavigationRequestOptions) => {
      managerRef.current?.requestHostNavigation(url, options);
    };
  }, []);

  const setGuidyRoutes = useMemo(() => {
    return (routes: GuidyRoute[]) => {
      managerRef.current?.setGuidyRoutes(routes);
    };
  }, []);

  const sendToDocy = useMemo(() => {
    return (payload: DocyrusSharePayload) => {
      return managerRef.current?.sendToDocy(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };
  }, []);

  const sendToChat = useMemo(() => {
    return (payload: DocyrusSharePayload) => {
      return managerRef.current?.sendToChat(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };
  }, []);

  const sendToEmail = useMemo(() => {
    return (payload: DocyrusEmailPayload) => {
      return managerRef.current?.sendToEmail(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };
  }, []);

  const sendToCalendar = useMemo(() => {
    return (payload: DocyrusCalendarEventPayload) => {
      return managerRef.current?.sendToCalendar(payload)
        ?? Promise.reject(new Error('Auth manager not initialized'));
    };
  }, []);

  const msGraph = useMemo(() => {
    return managerRef.current?.getMsGraphClient() ?? null;
  }, []);

  const value = useMemo<DocyrusAuthContextValue>(() => ({
    status,
    mode,
    client,
    tokens,
    user,
    signIn,
    signInWithTokens,
    getAuthorizationUrl,
    signOut,
    hasRole,
    hasPermission,
    refreshUser,
    onHostNavigation,
    onHostNotification,
    enableHostRouteSync,
    notifyRouteChange,
    requestHostNavigation,
    setGuidyRoutes,
    sendToDocy,
    sendToChat,
    sendToEmail,
    sendToCalendar,
    msGraph,
    error
  }), [
    status,
    mode,
    client,
    tokens,
    user,
    signIn,
    signInWithTokens,
    getAuthorizationUrl,
    signOut,
    hasRole,
    hasPermission,
    refreshUser,
    onHostNavigation,
    onHostNotification,
    enableHostRouteSync,
    notifyRouteChange,
    requestHostNavigation,
    setGuidyRoutes,
    sendToDocy,
    sendToChat,
    sendToEmail,
    sendToCalendar,
    msGraph,
    error
  ]);

  return (
    <DocyrusAuthContext value={value}>
      {children}
    </DocyrusAuthContext>
  );
}
